/**
 * Octoplus chat design tokens (direction v2 "calma", `brief/design-v2.md`).
 *
 * Values = main's chat palette (9f4b3e8): near-black / cool-grey panel, slate
 * text, neutral primary action, emerald for links and progress. Scoped to the
 * chat through `sx`, never the global MUI theme (app primary stays #1E4C7D).
 *
 * Rules:
 * - Purple (`brand`) is the mascot's colour only — never UI chrome (buttons,
 *   focus, balloons). `ink` is the neutral interface accent (primary action +
 *   focus ring), not a hue.
 * - Green (`link`, `chip`, `marker`, `ok`) is for links, progress dots and
 *   list markers.
 * - Text colours are solid hex (no rgba) so axe can measure contrast; all
 *   ≥4.5:1 on `bg`, `surface` and `raised`.
 * - `hairline` is decorative (exempt from contrast); `border` is ≥3:1.
 */
import { useTheme } from '@mui/material';

export type OctoMode = 'light' | 'dark';

export interface OctoColorTokens {
  bg: string;
  surface: string;
  raised: string;
  sunken: string;
  hairline: string;
  /** Hover state of hairline-bordered controls (decorative). */
  hairlineStrong: string;
  /** Borders of interactive controls (≥3:1). */
  border: string;
  /** Translucent fill of quiet controls (suggestions, chips). */
  fill: string;
  fillHover: string;
  text: string;
  text2: string;
  text3: string;
  /** Neutral interface accent: primary action background + focus ring. */
  ink: string;
  /** Primary action background (same as `ink`). */
  inkStrong: string;
  /** Primary action hover. */
  inkHover: string;
  onInk: string;
  /** Mascot purple. Never UI chrome. */
  brand: string;
  /** Hover / pressed / selected wash for quiet controls. */
  inkTint: string;
  /** Menus and lists (one shared style): hover, selected/current, pressed. */
  itemHover: string;
  itemSelected: string;
  itemPressed: string;
  /** User balloon. */
  bubble: string;
  onBubble: string;
  plateBg: string;
  plateFg: string;
  link: string;
  chip: string;
  /** Progress dots and list markers. */
  marker: string;
  ok: string;
  warn: string;
  err: string;
  /** Recording dot / level bars. */
  rec: string;
  /** Resting elevation (light only). */
  shadowRest: string;
  /** Panel / popovers. */
  shadowPopover: string;
  /** Expanded-mode backdrop dimmer (blur stays as today). */
  backdropDim: string;
}

export const octoColors: Record<OctoMode, OctoColorTokens> = {
  light: {
    bg: '#EFF0F2',
    surface: '#ffffff',
    raised: '#ffffff',
    sunken: '#eef1f4',
    hairline: 'rgba(15,23,42,0.08)',
    hairlineStrong: 'rgba(4,17,33,0.18)',
    border: '#7c8696',
    fill: 'rgba(255,255,255,0.85)',
    fillHover: '#ffffff',
    text: '#0f172a',
    text2: '#475569',
    text3: '#5b6474',
    ink: '#041121',
    inkStrong: '#041121',
    inkHover: '#0b1c33',
    onInk: '#ffffff',
    brand: '#7040FF',
    inkTint: 'rgba(15,23,42,0.06)',
    itemHover: 'rgba(15,23,42,0.04)',
    itemSelected: 'rgba(15,23,42,0.06)',
    itemPressed: 'rgba(15,23,42,0.09)',
    bubble: '#041121',
    onBubble: '#ffffff',
    plateBg: 'rgba(15,23,42,0.05)',
    plateFg: '#0f172a',
    link: '#047857',
    chip: '#047857',
    marker: '#059669',
    ok: '#047857',
    warn: '#92400e',
    err: '#b91c1c',
    rec: '#ef4444',
    shadowRest: '0 1px 2px rgba(15,23,42,0.04)',
    shadowPopover: '0 12px 40px rgba(15,23,42,0.12)',
    backdropDim: 'rgba(15,23,42,.5)',
  },
  dark: {
    bg: '#050505',
    surface: '#0f0f0f',
    raised: '#171717',
    sunken: '#0c0c0c',
    hairline: 'rgba(255,255,255,0.08)',
    hairlineStrong: 'rgba(255,255,255,0.22)',
    border: '#686c74',
    fill: 'rgba(255,255,255,0.07)',
    fillHover: 'rgba(255,255,255,0.1)',
    text: '#f8fafc',
    text2: '#b8bdc6',
    text3: '#8a8f97',
    ink: '#e2e8f0',
    inkStrong: '#e2e8f0',
    inkHover: '#f8fafc',
    onInk: '#0f172a',
    brand: '#7040FF',
    inkTint: 'rgba(255,255,255,0.08)',
    itemHover: 'rgba(255,255,255,0.06)',
    itemSelected: 'rgba(255,255,255,0.10)',
    itemPressed: 'rgba(255,255,255,0.13)',
    bubble: '#1e293b',
    onBubble: '#ffffff',
    plateBg: 'rgba(255,255,255,0.06)',
    plateFg: '#f8fafc',
    link: '#6ee7b7',
    chip: '#a7f3d0',
    marker: '#34d399',
    ok: '#6ee7b7',
    warn: '#fbbf24',
    err: '#fca5a5',
    rec: '#ef4444',
    shadowRest: 'none',
    shadowPopover: '0 12px 40px rgba(0,0,0,0.4)',
    backdropDim: 'rgba(0,0,0,.5)',
  },
};

/** Radii in px. Uniform corners (no held corner); pill for round buttons. */
export const octoRadius = {
  plate: 4,
  iconButton: 8,
  historyItem: 8,
  card: 12,
  code: 12,
  bubble: 18,
  /** User balloon: uniform 18 with a small tail corner (main). */
  bubbleTail: 6,
  proposal: 16,
  panel: 20,
  composer: 20,
  pill: 999,
} as const;

/** 4px grid (§5). Values in px. */
export const octoSpace = {
  unit: 4,
  panelX: 16,
  sameAuthorGap: 12,
  authorSwitchGap: 24,
} as const;

/** Motion (§6). Durations in ms. */
export const octoMotion = {
  easeTide: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeInk: 'cubic-bezier(0.4, 0, 0.2, 1)',
  hover: 120,
  state: 200,
  enter: 320,
  panel: 560,
} as const;

/** Typography (§4). System sans stack comes from the app; mono for identifiers. */
export const octoType = {
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  body: { fontSize: '0.9375rem', lineHeight: 1.6 },
  bodyExpanded: { fontSize: '1rem', lineHeight: 1.65 },
  greeting: { fontSize: '1.25rem', lineHeight: 1.3, letterSpacing: '-0.01em', fontWeight: 600 },
  /** Markdown headings inside assistant answers. */
  h2: { fontSize: '1.0625rem', lineHeight: 1.4, fontWeight: 650 },
  h3: { fontSize: '0.9375rem', lineHeight: 1.4, fontWeight: 650 },
  caption: { fontSize: '0.75rem', lineHeight: 1.4 },
  /** Step rail label: 500 while current, 400 once done. */
  step: { fontSize: '0.8125rem', lineHeight: 1.4 },
  plate: { fontSize: '0.8125rem', fontWeight: 500 },
  /** Max measure in fullscreen (§4). */
  maxMeasure: '72ch',
} as const;

export interface OctoTokens {
  mode: OctoMode;
  isDark: boolean;
  color: OctoColorTokens;
  radius: typeof octoRadius;
  space: typeof octoSpace;
  motion: typeof octoMotion;
  type: typeof octoType;
}

const buildTokens = (mode: OctoMode): OctoTokens => ({
  mode,
  isDark: mode === 'dark',
  color: octoColors[mode],
  radius: octoRadius,
  space: octoSpace,
  motion: octoMotion,
  type: octoType,
});

/** Frozen per-mode token objects: identity is stable, safe in deps arrays. */
const OCTO_TOKENS: Record<OctoMode, OctoTokens> = {
  light: buildTokens('light'),
  dark: buildTokens('dark'),
};

export const getOctoTokens = (mode: OctoMode): OctoTokens => OCTO_TOKENS[mode];

/**
 * Tokens for the current MUI palette mode. The chat follows the app theme:
 * `features/shared/context/ThemeContext.tsx` owns the mode (localStorage
 * `sistemat-theme-mode`) and feeds it to MUI `createTheme({ palette: { mode } })`,
 * so any component under the app ThemeProvider gets the right set. Outside a
 * provider (tests) MUI's default theme is light.
 */
export function useOctoTokens(): OctoTokens {
  const theme = useTheme();
  return getOctoTokens(theme.palette.mode === 'dark' ? 'dark' : 'light');
}

const kebab = (k: string) => k.replace(/([a-z])([A-Z0-9])/g, '$1-$2').toLowerCase();

/**
 * `--oc-*` CSS custom properties for a scope root (e.g. `--oc-ink`,
 * `--oc-text-2`, `--oc-plate-bg`). Spread into the root `sx`; descendants can
 * then use `var(--oc-ink)` in plain CSS/SVG. The JS tokens stay the primary API.
 */
export function octoCssVars(color: OctoColorTokens): Record<`--oc-${string}`, string> {
  const out: Record<string, string> = {};
  (Object.keys(color) as (keyof OctoColorTokens)[]).forEach((k) => {
    out[`--oc-${kebab(k)}`] = color[k];
  });
  return out as Record<`--oc-${string}`, string>;
}
